
import angulartics from 'angulartics';
import angularticsGoogleAnalytics from 'angulartics-google-analytics';
import 'angulartics-google-tag-manager';

import UserResource from '../common/resources/user.resource'

/**
 * Analytics service which configures angulartics or other modules related
 * to analytics.
 *
 * Dimensions are not used, all data is passed through tagmanager datalayer.
 */


const mod = angular.module('app.analytics.services', [
  angulartics,
  UserResource,
  angularticsGoogleAnalytics,
  'angulartics.google.tagmanager',
])


.config(function ($analyticsProvider) {
  // Configure any analytics settings here
  // Refer to http://luisfarzati.github.io/angulartics/#/plugins
  $analyticsProvider.withAutoBase(true);
  $analyticsProvider.settings.ga = {
    additionalAccountNames: undefined,
    // Select hits to send to all additional accounts
    additionalAccountHitTypes: {
      pageview: false,
      event: true,
      exception: false,
      ecommerce: false,
      userTiming: false,
      setUserProperties: false,
      userId: false
    },
    disableEventTracking: null,
    disablePageTracking: true,
    userId: null,
    enhancedEcommerce: true
  };
  $analyticsProvider.settings.pageTracking.autoTrackVirtualPages = false;
  $analyticsProvider.settings.pageTracking.autoTrackFirstPage = false;
})

.service('Analytics', function ($analytics, $log) {
  function createAnalyticsService() {
    const service = {};
    const dataLayer = window.dataLayer = window.dataLayer || [];

    service.setUser = (user, clearOnLogout = false) => {
      if (user != null) {
        $analytics.setUsername(user.id.toString());
      } else if (clearOnLogout === true) {
        $analytics.setUsername(null);
      }

      $log.debug(`$analytics.settings.ga.userId: ${$analytics.settings.ga.userId}`);
    }

    service.onUserChanged = function onUserChanged(u) {
      const authenticated = u != null;

      if (window.ga) {
        // No need to push them multiple times
        if (authenticated) {
          service.setUser(u)
          // birth year
          dataLayer.push({'by': u.birth_year});
          // gender
          dataLayer.push({'g': u.gender});
          dataLayer.push({'event': 'logged-in', 'userId': $analytics.settings.ga.userId});
        }
        else {
          dataLayer.push({'event': 'logged-out', 'userId': $analytics.settings.ga.userId});
          // Clear user from analytics after logged-out event so that it will still have the userId.
          service.setUser(u, true)
        }
      }

      $log.debug(`$analytics.settings.ga.userId: ${$analytics.settings.ga.userId} - authenticated: ${authenticated}`);
    };

    service.setLocationUnavailable = function (value) {
      if (window.ga) {
        dataLayer.push({'location_fetched': value});
      }
    };

    /**
     * Send prepopulated analytics messages.
     * @param category category name
     * @param action action name
     * @param label label name
     * @param value integer value relating to the data (optional)
     * @param extraData {Object} Object containing extra custom parameters that will
     *        be assigned into default payload. For example <code>{dimension4: "e"}</code>
     */
    service.postEvent = function (category, action, label, value, extraData) {
      if (!window.ga) {
        return;
      }

      let payload = {
        category: category,
        action: action,
        label: String(label),
        value: value == null ? null : String(value)
      };

      if (extraData != null) {
        payload = extraData.assign(payload);
      }

      $log.debug(`postEvent: $analytics.settings.ga.userId: ${$analytics.settings.ga.userId}`);
      $analytics.eventTrack(action, payload);
    };

    return service;
  }

  if (window.analyticsService != null) {
    $log.debug("AnalyticsService: already exists");
  }
  else {
    $log.debug("AnalyticsService: creating new");
    window.analyticsService = createAnalyticsService();
  }

  return window.analyticsService;
})

.service('FacebookPixel', function () {
  var service = {};
  // Track key page views (ex: product page, landing page or article)
  service.viewContent = function () {
    if (window.fbq != null) {
      window.fbq('track', 'ViewContent');
    }
  };

  // Track when a registration form is completed (ex. complete subscription, sign up for a service)
  service.completeRegistration = function () {
    if (window.fbq != null) {
      window.fbq('track', 'CompleteRegistration');
    }
  };

  return service;
})

.constant('ExperimentEnabled', true)

.service('GoogleExperiments', function ($log, $q, $timeout, DebugService, ExperimentEnabled) {
  var service = {};
  service.variation = 0;
  var deferredScript = $q.defer();

  var cxApi = window.cxApi;

  // Check if experiments script exists every second. Resolves script when done or 10 seconds
  // have passed.
  var times = 10;

  function _checkForScript() {
    $timeout(function () {
      if (window.cxApi) {
        service.checkVariant();
      }
      else {
        if (times > 0) {
          times--;
          _checkForScript();
        }
        else{
          $log.error("Could not load Google Experiments. This could because tracking is disabled by user");
          service.variation = 0;
          deferredScript.resolve(service.variation);
        }
      }
    }, 500);
  }

  service.checkVariant = function () {
    cxApi = window.cxApi;

    if (angular.isDefined(cxApi.chooseVariation)) {
      $log.log("chosen, new:", cxApi.getChosenVariation(), cxApi.chooseVariation());
      var variation = cxApi.chooseVariation();

      // Enable variation if debug is enabled
      if (variation === 0 && DebugService.enabled === true) {
        $log.debug("Variant was " + 0 + ", enabling experiment");
        cxApi.setChosenVariation(1);
      }

      variation = cxApi.chooseVariation();
      service.variation = variation;
      deferredScript.resolve(variation);
    }
    else {
      $log.warn("No CxApi, check that ga and gaExperiment app configuration variables are set");
    }
  };

  /**
   * Get the experiment value and make sure that the Experiments API is loaded before.
   * @returns {Promise} Promise that will be resolved with variation when loading is finished.
   */
  service.getExperiments = function () {
    if (!ExperimentEnabled) {
      // Everyone enters experiment when experiment is disabled
      service.variation = 1;
      deferredScript.resolve(1);
    }
    else {
      if (window.cxApi) {
        service.checkVariant();
      }
      else {
        _checkForScript();
      }
    }
    return deferredScript.promise;
  };

  return service;
})

;

export default mod.name;
